import { defineStore } from 'pinia'
import {
  LocationQuery,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteMeta,
  RouteParams,
  Router,
  RouteRecordName,
  RouteRecordNormalized
} from 'vue-router'
import { TAB_KEY } from '@/enums/cache'
import store from '@/store'
import router from '@/router'
import { ANONYMOUS_ROUTES } from '@/router/routes'
import { getAuthCache, setAuthCache } from '@/utils/auth'
import { pick } from 'ramda'
import useMenu from '@/hooks/useMenu'
const { checkRouteAvailable, checkPathAvailable } = useMenu()

interface visitedRoute {
  path: string
  fullPath: string
  name: RouteRecordName | null | undefined
  params: RouteParams
  query: LocationQuery
  meta: RouteMeta
}
export interface Tab extends RouteLocationNormalized {
  visitedRecords: visitedRoute[]
}

interface TabsViewState {
  tabs: Tab[]
  cacheTabs: Set<string>
}
function getTabRecord(route: RouteLocationNormalized) {
  return pick(['path', 'fullPath', 'name', 'params', 'query', 'meta'], route)
}
function getVisitedRecords(route: RouteLocationNormalized) {
  return getTabRecord(route)
}

function findCurrentRouteTabIndexOf(path: string, tabs: Tab[]) {
  return tabs.findIndex((tab) => {
    return tab.path === path || tab.visitedRecords?.some((record) => record.path === path)
  })
}

export const useTabsViewStore = defineStore({
  id: 'tabs',
  state: (): TabsViewState => ({
    tabs: getAuthCache<Tab[]>(TAB_KEY) || [],
    cacheTabs: new Set([])
  }),
  getters: {
    getTabs(): Tab[] {
      return this.tabs.length ? this.tabs : getAuthCache<Tab[]>(TAB_KEY) || []
    },
    getCacheTabs(): string[] {
      return Array.from(this.cacheTabs)
    }
  },
  actions: {
    async addTab(route: RouteLocationNormalized) {
      const { path, name, fullPath, params, query, meta = {} } = route
      if (['/login', '/404'].includes(path)) {
        return
      }

      if (ANONYMOUS_ROUTES.includes(name as string)) {
        return
      }

      const { parentMenu } = meta
      const realPath = parentMenu ? parentMenu : path
      const currentTabRoute = router.getRoutes().find((item) => {
        const matchPath = item.meta?.fuzzyMatch ? realPath.split('?')[0] : realPath
        return item.path === matchPath
      })

      const tab = this.tabs.find(
        (item) => item.name === (currentTabRoute as RouteRecordNormalized).name
      )
      if (tab) {
        if (parentMenu) {
          const { visitedRecords } = tab
          const recordIndex = visitedRecords.findIndex((item) => item.name === name)
          if (recordIndex === -1) {
            visitedRecords.push(getVisitedRecords(route))
          } else {
            visitedRecords[recordIndex] = getVisitedRecords(route)
            tab.visitedRecords = visitedRecords.slice(0, recordIndex + 1)
          }
        } else {
          tab.fullPath = fullPath
          tab.params = params
          tab.query = query
          tab.visitedRecords = [getVisitedRecords(route)]
        }
      } else {
        // 权限控制，只有有权限的菜单才能被添加，防止通过输入网址访问页面
        // 兼容「菜单父路由 redirect 到子路由」场景：点击一级菜单会先进入父路由，再重定向到子路由，最终route.path可能不在menuMap中，此时使用 redirectedFrom.path 作为兜底权限判断，避免标签页丢失
        const redirectedFromPath = route.redirectedFrom?.path
        const isAvailable =
          checkRouteAvailable(route) ||
          (redirectedFromPath ? checkPathAvailable(redirectedFromPath) : false)
        if (isAvailable) {
          this.tabs.push({
            ...getTabRecord(route),
            visitedRecords: [getVisitedRecords(route)]
          } as never)
        }
      }

      setAuthCache(TAB_KEY, this.tabs)

      this.updateCacheTabs()
    },
    async removeTab(tab: Tab, Router: Router = router) {
      const close = (route: RouteLocationNormalized) => {
        const { path } = route
        const index = findCurrentRouteTabIndexOf(path, this.tabs)
        index !== -1 && this.tabs.splice(index, 1)
      }

      const { currentRoute, replace } = Router
      const { path } = unref(currentRoute)
      if (findCurrentRouteTabIndexOf(path, [tab]) === -1) {
        close(tab)
        return
      }

      let toTarget: RouteLocationRaw = {}
      // 处理最左边的标签
      const index = findCurrentRouteTabIndexOf(path, this.tabs)
      if (index === 0) {
        if (this.tabs.length === 1) {
          // to home page
          toTarget = '/'
        } else {
          const page = this.tabs[index + 1]
          toTarget = getToTarget(page)
        }
      } else {
        const page = this.tabs[index - 1]
        toTarget = getToTarget(page)
      }

      close(currentRoute.value)
      setAuthCache(TAB_KEY, this.tabs)
      this.updateCacheTabs()
      await replace(toTarget)
    },
    async updateCacheTabs() {
      const cacheSet: Set<string> = new Set()

      for (const tab of this.tabs) {
        const routes = tab.visitedRecords
        routes.forEach((route) => {
          const needCache = !route.meta.noCache
          const name = route.name as string
          needCache && cacheSet.add(name)
        })
      }

      this.cacheTabs = cacheSet
    },
    clearCacheTabs() {
      this.cacheTabs = new Set()
    },
    resetState() {
      this.tabs = []
      this.clearCacheTabs()
      setAuthCache(TAB_KEY, [])
    }
  }
})

function getToTarget(tab: Tab) {
  const { params, path, query, visitedRecords } = tab
  // const lastRecord = visitedRecords.at(-1)
  const lastRecord = visitedRecords[visitedRecords.length - 1]
  if (lastRecord) {
    return {
      params: lastRecord.params,
      path: lastRecord.path,
      query: lastRecord.query
    }
  }

  return {
    params: params || {},
    path,
    query: query || {}
  }
}

export function useTabsViewStoreWithoutSetup() {
  return useTabsViewStore(store)
}
